import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useNotificationList } from '@/hooks/useNotificationList';
import { useUnreadCount } from '@/hooks/useUnreadCount';
import { markRead, markAllRead } from '@/services/notificationApi';
import type { NotificationOut } from '@/types/notification';
import Spinner from '@/components/ui/Spinner';
import Alert from '@/components/ui/Alert';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getNotificationTitle(notification: NotificationOut): string {
  const { type, payload } = notification;
  // Goal E (E4): coach-submits-for-athlete. The result belongs to the
  // athlete, not the coach — name the template so the bell reads as "this
  // happened to you", not a generic activity ping.
  if (type === 'eval_completed_on_behalf') {
    const name = payload.coach_display_name ?? 'Your coach';
    if (payload.template_name) {
      return `${name} evaluated your ${payload.template_name} performance`;
    }
    return `${name} evaluated you`;
  }
  if (type === 'eval_completed_self') {
    return 'Your evaluation completed';
  }
  if (type === 'evaluation_shared_with_you') {
    const athlete = payload.athlete_display_name ?? 'An athlete';
    return `${athlete} shared an evaluation`;
  }
  if (type === 'channel_member_added') {
    const channelName = payload.channel_name ?? 'a channel';
    if (payload.inviter_display_name) {
      return `${payload.inviter_display_name} added you to ${channelName}`;
    }
    return `You joined ${channelName}`;
  }
  if (type === 'channel_announcement') {
    const author = payload.author_display_name ?? 'Your coach';
    const channelName = payload.channel_name ?? 'your channel';
    return `${author} posted to ${channelName}`;
  }
  if (type === 'eval_failed') {
    return 'Evaluation failed';
  }
  // B-2026-0072: leaderboard rank improvement
  if (type === 'leaderboard_rank_improved') {
    const channelName = payload.channel_name ?? 'your channel';
    return `You moved up on ${channelName}`;
  }
  // B-2026-0075: milestone achieved
  if (type === 'milestone_achieved') {
    const icon = payload.milestone_icon_key ?? '🏅';
    const label = payload.milestone_label ?? 'Badge';
    return `${icon} ${label}`;
  }
  // Batch 5/6: coach re-engagement nudge (POST /api/notifications/send-encouragement)
  if (type === 'coach_re_engagement') {
    const name = payload.coach_display_name ?? 'Your coach';
    return `${name} is thinking of you`;
  }
  // Goal Score go-live B1: coach-submitted eval awaiting the athlete's Yes/No
  if (type === 'publish_consent_requested') {
    return 'Your coach shared a score with you';
  }
  return 'New notification';
}

function getNotificationBody(notification: NotificationOut): string {
  const { type, payload } = notification;
  // channel_member_added: channel name is already in the title; body is empty.
  if (type === 'channel_member_added') {
    return '';
  }
  // channel_announcement: show truncated post body preview
  if (type === 'channel_announcement') {
    return payload.post_body ?? '';
  }
  if (type === 'eval_failed') {
    return payload.error_message ?? 'Unknown error';
  }
  // B-2026-0072: leaderboard rank improvement
  if (type === 'leaderboard_rank_improved') {
    const oldRank = payload.old_rank;
    const newRank = payload.new_rank;
    const score = payload.new_score;
    if (oldRank != null && newRank != null) {
      const scoreStr = score != null ? ` · ${score.toFixed(1)}/100` : '';
      return `Rank #${oldRank} → #${newRank}${scoreStr}`;
    }
    return '';
  }
  // B-2026-0075: milestone achieved — show the description
  if (type === 'milestone_achieved') {
    return 'New badge earned! View your badges on your dashboard.';
  }
  // Batch 5/6: the backend already rendered the full sentence.
  if (type === 'coach_re_engagement') {
    return payload.rendered_message ?? 'Come back and submit an evaluation!';
  }
  // Goal Score go-live B1: the plain what/why/how prompt.
  if (type === 'publish_consent_requested') {
    return 'Do you want to publish it on the leaderboard? Yes or no is up to you.';
  }
  const templateName = payload.template_name ?? 'Template';
  if (payload.score != null) {
    return `${templateName} · ${payload.score.toFixed(1)}/100`;
  }
  return templateName;
}

function getNavTarget(notification: NotificationOut): string {
  const { type, payload } = notification;
  if (type === 'channel_member_added' && payload.channel_slug) {
    return `/channels/${payload.channel_slug}`;
  }
  // channel_announcement: navigate to the channel's Posts tab
  if (type === 'channel_announcement' && payload.channel_slug) {
    return `/channels/${payload.channel_slug}?tab=posts`;
  }
  if (type === 'eval_failed') {
    return '/evaluations';
  }
  // B-2026-0072: leaderboard rank → channel leaderboard tab
  if (type === 'leaderboard_rank_improved' && payload.channel_slug) {
    return `/channels/${payload.channel_slug}?tab=leaderboard`;
  }
  // B-2026-0075: milestone → athlete dashboard (badges section)
  if (type === 'milestone_achieved') {
    return '/dashboard';
  }
  // Batch 5/6: the nudge asks the athlete to submit an evaluation.
  if (type === 'coach_re_engagement') {
    return '/evaluate';
  }
  // Goal Score go-live B1: nav straight to the evaluation's results page,
  // where GoalScorePublishConsent renders the Yes/No for an awaiting_athlete eval.
  if (type === 'publish_consent_requested' && payload.evaluation_id) {
    return `/evaluations/${payload.evaluation_id}`;
  }
  // Goal E (E4): same destination as publish_consent_requested — the
  // athlete lands on the same results page either way. Explicit branch
  // (rather than relying on the generic fallback below) so the two
  // coach-submitted-for-athlete notification types stay visibly paired.
  if (type === 'eval_completed_on_behalf' && payload.evaluation_id) {
    return `/evaluations/${payload.evaluation_id}`;
  }
  if (payload.evaluation_id) {
    return `/evaluations/${payload.evaluation_id}`;
  }
  return '/';
}

/** True when the notification should use a celebratory accent style. */
function isCelebratory(type: string): boolean {
  return type === 'milestone_achieved' || type === 'leaderboard_rank_improved';
}

// ─── NotificationItem ─────────────────────────────────────────────────────────

interface NotificationItemProps {
  notification: NotificationOut;
  onMarkRead: (id: string) => void;
  onClose: () => void;
}

function NotificationItem({ notification, onMarkRead, onClose }: NotificationItemProps) {
  const isUnread = notification.read_at === null;
  const title = getNotificationTitle(notification);
  const body = getNotificationBody(notification);
  const navTarget = getNavTarget(notification);
  const relativeTime = formatDistanceToNow(new Date(notification.created_at), {
    addSuffix: true,
  });

  function handleClick() {
    onMarkRead(notification.id);
    onClose();
  }

  const isChannelAnnouncement = notification.type === 'channel_announcement';
  const isMilestone = notification.type === 'milestone_achieved';
  const isCelebratoryType = isCelebratory(notification.type);

  return (
    <Link
      to={navTarget}
      onClick={handleClick}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '12px',
        minHeight: '44px',
        padding: '12px 16px',
        textDecoration: 'none',
        borderLeft: isMilestone
          ? '2px solid var(--color-accent)'
          : isUnread
          ? '2px solid var(--color-accent)'
          : '2px solid transparent',
        backgroundColor: isCelebratoryType
          ? 'rgba(30,215,96,0.04)'
          : 'transparent',
        transition: 'background-color 120ms ease',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.backgroundColor =
          'var(--color-dark-elevated-2)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: isMilestone ? '14px' : '13px',
            fontWeight: isUnread || isMilestone ? 600 : 400,
            color: isMilestone ? 'var(--color-accent)' : 'var(--color-text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 1.4,
          }}
        >
          {title}
        </div>
        {body && (
          <div
            style={{
              fontSize: '12px',
              color: 'var(--color-text-secondary)',
              marginTop: '2px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {body}
          </div>
        )}
        {/* channel_announcement: explicit "View Channel" CTA */}
        {isChannelAnnouncement && (
          <div
            style={{
              marginTop: '6px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '11px',
              fontWeight: 600,
              color: 'var(--color-accent)',
            }}
          >
            View Channel →
          </div>
        )}
      </div>
      <div
        style={{
          fontSize: '11px',
          color: 'var(--color-text-muted)',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          paddingTop: '1px',
        }}
      >
        {relativeTime}
      </div>
    </Link>
  );
}

// ─── NotificationDropdown ────────────────────────────────────────────────────

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
  dropdownId: string;
}

function NotificationDropdown({
  isOpen,
  onClose,
  containerRef: _containerRef,
  dropdownId,
}: NotificationDropdownProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: listData, isLoading, isError } = useNotificationList(isOpen);
  const { data: countData } = useUnreadCount();
  const unreadCount = countData?.unread_count ?? 0;

  // Mutation: mark single notification read
  const markReadMutation = useMutation({
    mutationFn: markRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-unread-count', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['notification-list', user?.id] });
    },
  });

  // Mutation: mark all read
  const markAllReadMutation = useMutation({
    mutationFn: markAllRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-unread-count', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['notification-list', user?.id] });
    },
  });

  // Escape key closes dropdown
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const notifications = listData ?? [];

  return (
    <div
      id={dropdownId}
      role="region"
      aria-label="Notifications"
      className="sf-dropdown"
      style={{
        position: 'absolute',
        right: 0,
        top: 'calc(100% + 10px)',
        zIndex: 40,
        width: '360px',
        maxWidth: 'calc(100vw - 16px)',
        borderRadius: '14px',
        border: '1px solid var(--color-border-subtle)',
        backgroundColor: 'var(--color-dark-elevated-1)',
        boxShadow: 'var(--shadow-modal)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 16px 10px',
          borderBottom: '1px solid var(--color-border-subtle)',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-body)',
            fontWeight: 600,
            fontSize: '14px',
            color: 'var(--color-text-primary)',
          }}
        >
          Notifications
        </span>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isPending}
            style={{
              fontSize: '12px',
              fontWeight: 500,
              color: 'var(--color-accent)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px 0',
              opacity: markAllReadMutation.isPending ? 0.5 : 1,
            }}
          >
            Mark all read
          </button>
        )}
      </div>

      {/* Error state */}
      {isError && (
        <div style={{ padding: '10px 12px' }}>
          <Alert variant="error" className="text-xs">
            Could not load notifications
          </Alert>
        </div>
      )}

      {/* List body */}
      <div
        style={{
          maxHeight: '480px',
          overflowY: 'auto',
        }}
      >
        {isLoading && !isError ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '32px 16px',
            }}
          >
            <Spinner size="sm" />
          </div>
        ) : !isError && notifications.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '32px 16px',
              fontSize: '13px',
              color: 'var(--color-text-muted)',
            }}
          >
            No notifications
          </div>
        ) : (
          notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkRead={(id) => markReadMutation.mutate(id)}
              onClose={onClose}
            />
          ))
        )}
      </div>

      {/* Footer: cap notice */}
      {!isLoading && notifications.length === 20 && (
        <div
          style={{
            textAlign: 'center',
            padding: '10px 16px',
            fontSize: '11px',
            color: 'var(--color-text-muted)',
            borderTop: '1px solid var(--color-border-subtle)',
          }}
        >
          Showing latest 20
        </div>
      )}
    </div>
  );
}

export default NotificationDropdown;
